import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Download, Plus, Shield, ShieldCheck,
  FileText, AlertTriangle, Eye, Archive, ArrowRight,
  Server, Cpu, Database, Router, Wifi, Loader2, RefreshCw
} from 'lucide-react';
import { apiClient } from '../api';
import RiskBadge from '../components/UI/RiskBadge';

const INPUT_CLS = 'bg-gray-950 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500 transition-colors';
const SELECT_CLS = 'px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-gray-300 focus:outline-none focus:border-cyan-500 transition-colors';

const CRITICALITY_STYLES = {
  CRITICAL: 'bg-red-400/10 text-red-400 border border-red-400/20',
  HIGH:     'bg-orange-400/10 text-orange-400 border border-orange-400/20',
  MEDIUM:   'bg-amber-400/10 text-amber-400 border border-amber-400/20',
  LOW:      'bg-emerald-400/10 text-emerald-400 border border-emerald-400/20',
};

const TYPE_ICON = {
  linux: Server,
  windows: Cpu,
  server: Server,
  database: Database,
  network_device: Router,
  endpoint: Cpu,
  iot: Wifi,
};

const getRiskLevel = (score) => {
  if (score == null || score <= 25) return 'LOW';
  if (score <= 50) return 'MEDIUM';
  if (score <= 75) return 'HIGH';
  return 'CRITICAL';
};

const MyAssets = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [criticalityFilter, setCriticalityFilter] = useState('all');
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [serversRes, riskRes] = await Promise.allSettled([
        apiClient.get('/api/servers'),
        apiClient.get('/api/dashboard/risk'),
      ]);

      if (serversRes.status === 'rejected') {
        setError('Failed to load assets');
        setLoading(false);
        return;
      }

      const servers = serversRes.value.data.servers || [];
      const riskMap = new Map();

      if (riskRes.status === 'fulfilled') {
        const riskServers = riskRes.value.data.servers || [];
        riskServers.forEach(s => riskMap.set(s.id, s));
      }

      const mappedAssets = servers.map(s => {
        const risk = riskMap.get(s.id);
        return {
          id: s.id,
          name: s.hostname,
          ip: s.ip_address || '—',
          type: s.os_type || 'server',
          risk_score: risk?.risk_score ?? 0,
          active_ioc_count: risk?.active_ioc_count ?? 0,
          active_cve_count: risk?.active_cve_count ?? 0,
          criticality: getRiskLevel(risk?.risk_score ?? 0),
          last_seen: s.last_seen,
          created_at: s.created_at,
        };
      });

      setAssets(mappedAssets);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filteredAssets = assets.filter(a => {
    const matchSearch = a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        a.ip.includes(searchTerm);
    const matchCrit = criticalityFilter === 'all' || a.criticality === criticalityFilter;
    return matchSearch && matchCrit;
  });

  const stats = {
    total: assets.length,
    critical: assets.filter(a => a.criticality === 'CRITICAL').length,
    highRisk: assets.filter(a => a.risk_score > 50).length,
    online: assets.filter(a => a.last_seen).length,
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          <p className="text-gray-500 text-sm">Loading assets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-64 gap-4">
        <AlertTriangle className="w-10 h-10 text-red-400" />
        <p className="text-gray-400">{error}</p>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 text-sm text-cyan-400 bg-cyan-400/10 border border-cyan-400/20 rounded-lg hover:bg-cyan-400/20 transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">My Assets</h1>
          <p className="text-sm text-gray-500 mt-1">
            Registered assets discovered via Network Map
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:border-gray-600 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => navigate('/network-map')}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:border-gray-600 transition-colors"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Go to Network Map
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-gray-900 border border-gray-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total</p>
            <Server className="w-4 h-4 text-cyan-400" />
          </div>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </div>
        <div className="bg-gray-900 border border-gray-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Critical</p>
            <AlertTriangle className="w-4 h-4 text-red-400" />
          </div>
          <p className="text-2xl font-bold text-red-400">{stats.critical}</p>
        </div>
        <div className="bg-gray-900 border border-gray-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-gray-500 uppercase tracking-wider">High Risk</p>
            <Shield className="w-4 h-4 text-orange-400" />
          </div>
          <p className="text-2xl font-bold text-orange-400">{stats.highRisk}</p>
        </div>
        <div className="bg-gray-900 border border-gray-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Online</p>
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
          </div>
          <p className="text-2xl font-bold text-emerald-400">{stats.online}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by name or IP..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={`w-full pl-9 pr-4 py-2 ${INPUT_CLS}`}
          />
        </div>
        <select value={criticalityFilter} onChange={e => setCriticalityFilter(e.target.value)} className={SELECT_CLS}>
          <option value="all">All Criticality</option>
          <option value="CRITICAL">Critical</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
        </select>
      </div>

      {/* Assets table */}
      <div className="bg-gray-900 border border-gray-700/50 rounded-xl overflow-hidden">
        {filteredAssets.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
            {assets.length === 0 ? 'No assets registered. Agents self-register via API key.' : 'No assets match your filters.'}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700/50">
                {['Asset Name', 'IP Address', 'Risk', 'Criticality', 'IoC', 'CVE', 'Last Seen', 'Actions'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAssets.map(a => {
                const Icon = TYPE_ICON[a.type] || Server;
                return (
                  <tr key={a.id} className="border-b border-gray-700/30 hover:bg-gray-800/30 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-white">{a.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <code className="text-xs bg-gray-800 border border-gray-700 px-2 py-0.5 rounded text-gray-300">
                        {a.ip}
                      </code>
                    </td>
                    <td className="px-5 py-3.5">
                      <RiskBadge score={a.risk_score} />
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${CRITICALITY_STYLES[a.criticality]}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        {a.criticality}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      {a.active_ioc_count > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-400/10 text-red-400 border border-red-400/20">
                          <AlertTriangle className="w-3 h-3" />
                          {a.active_ioc_count}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      {a.active_cve_count > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-amber-400/10 text-amber-400 border border-amber-400/20">
                          <FileText className="w-3 h-3" />
                          {a.active_cve_count}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-xs text-gray-500">
                        {a.last_seen ? new Date(a.last_seen).toLocaleString() : 'Never'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <button
                        onClick={() => navigate(`/assets/${a.id}`)}
                        className="px-2.5 py-1 text-xs text-cyan-400 bg-cyan-400/10 border border-cyan-400/20 rounded-md hover:bg-cyan-400/20 transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Help text */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-cyan-400/10 flex items-center justify-center shrink-0">
            <Plus className="w-4 h-4 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-white">How assets appear here</h3>
            <p className="text-xs text-gray-500 mt-1">
              Agents self-register via API key when they send their first heartbeat or snapshot. 
              Risk scores are calculated from active IoC and CVE alerts. 
              Click <button onClick={() => navigate('/network-map')} className="text-cyan-400 hover:underline">Network Map</button> to view all discovered servers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAssets;
